import { useRequiredContext } from "@redotech/react-util/context";
import {
  AppFieldInstance,
  useAppForm,
} from "@redotech/redo-merchant-app-common/hooks/use-app-form";
import type { SupportFolder } from "@redotech/redo-support-common/support-folder";
import {
  CheckboxValue,
  RedoCheckbox,
} from "@redotech/redo-web/arbiter-components/checkbox/redo-checkbox";
import {
  RedoModal,
  RedoModalButtonProps,
} from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useMemo, useState } from "react";
import { ConfirmDeleteFolderModal } from "./confirm-delete-folder-modal";
import { viewUrlNameToDisplayName } from "./view-name-utils";
import { DefaultViews, ViewsContext } from "./views";

const defaultViewValues = new Set<string>(Object.values(DefaultViews));

function isDefaultView(id: string): boolean {
  return defaultViewValues.has(id);
}

interface EditFolderModalProps {
  isOpen: boolean;
  onClose: () => void;
  folder: SupportFolder;
  onSubmit: (updates: {
    name?: string;
    viewIds?: string[];
    defaultViews?: string[];
  }) => Promise<void>;
  deleteFolder: (folderId: string) => Promise<void>;
}

function buildInitialSelectedViews(
  folder: SupportFolder,
): Record<string, boolean> {
  const selected: Record<string, boolean> = {};
  for (const viewId of folder.viewIds) {
    selected[viewId] = true;
  }
  for (const defaultView of folder.defaultViews) {
    selected[defaultView] = true;
  }
  return selected;
}

export const EditFolderModal = memo(function EditFolderModal({
  isOpen,
  onClose,
  folder,
  onSubmit,
  deleteFolder,
}: EditFolderModalProps) {
  const viewsContext = useRequiredContext(ViewsContext);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);

  const allViews = useMemo(
    () => [
      ...Object.values(DefaultViews).map((view) => ({
        _id: view,
        name: viewUrlNameToDisplayName(view),
      })),
      ...viewsContext.map((view) => ({ _id: view._id, name: view.name })),
    ],
    [viewsContext],
  );

  const form = useAppForm({
    defaultValues: {
      name: folder.name,
      selectedViews: buildInitialSelectedViews(folder),
    },
    onSubmit: async ({
      value,
    }: {
      value: { name: string; selectedViews: Record<string, boolean> };
    }) => {
      const selectedIds = Object.entries(value.selectedViews)
        .filter(([, checked]) => checked)
        .map(([viewId]) => viewId);

      const defaultViews = selectedIds.filter(isDefaultView);
      const viewIds = selectedIds.filter((id) => !isDefaultView(id));

      if (viewIds.length === 0 && defaultViews.length === 0) {
        await deleteFolder(folder._id);
        onClose();
        return;
      }

      await onSubmit({ name: value.name, viewIds, defaultViews });
      onClose();
    },
  });

  if (!isOpen) {
    return null;
  }

  return (
    <form.AppForm>
      <form.Subscribe
        selector={(state: {
          isSubmitting: boolean;
          canSubmit: boolean;
          values: { name: string; selectedViews: Record<string, boolean> };
        }) => ({
          isSubmitting: state.isSubmitting,
          canSubmit: state.canSubmit,
          name: state.values.name,
          selectedViews: state.values.selectedViews,
        })}
      >
        {({
          isSubmitting,
          canSubmit,
          name,
          selectedViews,
        }: {
          isSubmitting: boolean;
          canSubmit: boolean;
          name: string;
          selectedViews: Record<string, boolean>;
        }) => {
          const submitDisabled = !name.trim().length || !canSubmit;

          const primaryButton: RedoModalButtonProps = {
            text: "Submit",
            onClick: () => form.handleSubmit(),
            disabled: submitDisabled,
            pending: isSubmitting,
            size: "sm",
          };

          const secondaryButton: RedoModalButtonProps = {
            text: "Delete",
            onClick: () => setShowConfirmDelete(true),
            disabled: isSubmitting,
            theme: "destructive",
            size: "sm",
          };

          return (
            <>
              <RedoModal
                isOpen
                modalSize="sm"
                onModalCloseRequested={isSubmitting ? () => {} : onClose}
                primaryButton={primaryButton}
                secondaryButton={secondaryButton}
                title="Edit folder"
              >
                <Flex dir="column" gap="xl">
                  <form.AppField name="name">
                    {(field: AppFieldInstance) => (
                      <field.TextField
                        label="Folder name"
                        placeholder="Enter folder name"
                        size="sm"
                      />
                    )}
                  </form.AppField>
                  <Text>Select views</Text>
                  <Flex dir="column" gap="md" pl="sm">
                    {allViews.map((view) => (
                      <RedoCheckbox
                        key={view._id}
                        label={view.name}
                        setValue={(val: CheckboxValue) =>
                          form.setFieldValue(
                            `selectedViews.${view._id}`,
                            val === true,
                          )
                        }
                        size="sm"
                        value={selectedViews[view._id] ?? false}
                      />
                    ))}
                  </Flex>
                </Flex>
              </RedoModal>
              <ConfirmDeleteFolderModal
                folderName={name}
                isOpen={showConfirmDelete}
                onClose={() => setShowConfirmDelete(false)}
                onConfirm={async () => {
                  await deleteFolder(folder._id);
                  onClose();
                }}
              />
            </>
          );
        }}
      </form.Subscribe>
    </form.AppForm>
  );
});
